import React from 'react';
import { Box, Text, VStack, HStack, Icon, Button, Collapse, Code } from '@chakra-ui/react';
import { FiCheckCircle, FiAlertTriangle, FiXCircle, FiInfo, FiCopy, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import useAppToast from 'components/toast';
import Card from 'components/card/Card';

/**
 * Why WhatsApp is not ready yet — one line per check, with the fix.
 *
 * ★ A DEAD END IS A DEFECT. "Ask your platform operator" leaves an
 * administrator with nothing to relay: they cannot tell whether the address is
 * missing on the CRM, the feature is off on the other service, or the two hold
 * different secrets. Each has a different fix, so each gets its own line, named
 * by variable and by service.
 *
 * Everything here is a variable NAME or a boolean, never a value, so it is safe
 * on a tenant's screen. **Copy for your operator** puts the unresolved lines on
 * the clipboard, because the person who fixes them is usually not the person
 * looking at this.
 */

const STATE = {
	ok: { icon: FiCheckCircle, color: 'success', ring: 'success.subtle' },
	warn: { icon: FiAlertTriangle, color: 'warning', ring: 'warning.subtle' },
	error: { icon: FiXCircle, color: 'danger', ring: 'danger.subtle' },
	info: { icon: FiInfo, color: 'info', ring: 'info.subtle' },
};

function Row({ item }) {
	const state = STATE[item.status] ?? STATE.info;
	return (
		<HStack align="start" spacing="3" px="3" py="2.5" borderRadius="md" bg={item.status === 'ok' ? 'transparent' : state.ring}>
			<Box
				w="22px"
				h="22px"
				borderRadius="full"
				bg={item.status === 'ok' ? 'transparent' : 'bg.surface'}
				display="flex"
				alignItems="center"
				justifyContent="center"
				flexShrink={0}
				mt="0.5"
			>
				<Icon as={state.icon} color={state.color} boxSize="4" />
			</Box>
			<Box minW="0" flex="1">
				<Text fontSize="sm" color="text.primary" fontWeight={item.status === 'ok' ? 'normal' : 'medium'}>
					{item.label}
				</Text>
				{item.detail && (
					<Text fontSize="xs" color="text.secondary" mt="0.5">
						{item.detail}
					</Text>
				)}
				{item.fix && (
					<Text fontSize="xs" color="text.muted" mt="1">
						<Box as="span" fontWeight="medium" color="text.secondary">
							Fix:{' '}
						</Box>
						{item.fix}
					</Text>
				)}
			</Box>
		</HStack>
	);
}

export default function SetupChecklist({ diagnostics, defaultOpen = true }) {
	const notify = useAppToast();
	const [open, setOpen] = React.useState(defaultOpen);
	const items = diagnostics?.items ?? [];
	if (!items.length) return null;

	const blocking = items.filter((i) => i.status === 'error');
	const warnings = items.filter((i) => i.status === 'warn');
	const ready = blocking.length === 0;

	// ★ WHOSE JOB IS OUTSTANDING. Items whose id starts `tenant.` are set by the
	// person reading this, here in the CRM; everything else is a server variable
	// set once by whoever runs them. Telling a workspace administrator to "ask
	// your operator" about their own token sends them to the wrong person.
	const mine = (i) => String(i.id ?? '').startsWith('tenant.');
	const outstanding = [...blocking, ...warnings];
	const anyMine = outstanding.some(mine);
	const anyOperator = outstanding.some((i) => !mine(i));

	const headline = ready
		? warnings.length
			? 'WhatsApp is ready, with a few things still to set'
			: 'Everything is configured'
		: blocking.length === 1
			? '1 thing must be set before a number can be connected'
			: `${blocking.length} things must be set before a number can be connected`;

	const copy = async () => {
		// Only the server-side lines: an operator can do nothing about a token
		// that lives in this workspace's own settings.
		const lines = outstanding
			.filter((i) => !mine(i))
			.map((i) => `- ${i.label}: ${i.detail}${i.fix ? `\n  Fix: ${i.fix}` : ''}`);
		try {
			await navigator.clipboard.writeText(
				`Meta WhatsApp setup — outstanding items\n\n${lines.join('\n')}\n`,
			);
			notify.success('Copied', 'Send this to whoever configures the servers.');
		} catch (_) {
			notify.error('Not copied', 'Select the text and copy it manually.');
		}
	};

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor={ready ? 'border.default' : 'danger'}>
			<Box p="4">
				<HStack justify="space-between" align="start" spacing="3">
					<HStack spacing="3" align="start" minW="0">
						<Icon
							as={ready ? (warnings.length ? FiAlertTriangle : FiCheckCircle) : FiXCircle}
							color={ready ? (warnings.length ? 'warning' : 'success') : 'danger'}
							boxSize="5"
							mt="0.5"
						/>
						<Box minW="0">
							<Text fontSize="sm" fontWeight="bold" color="text.primary">
								{headline}
							</Text>
							<Text fontSize="xs" color="text.muted" mt="0.5">
								{anyMine && anyOperator
									? 'Some of these you set here in the CRM; the rest are server settings, set once by whoever runs the servers.'
									: anyMine
										? 'You can fix this yourself, here in the CRM — no server access needed.'
										: 'These are server settings, not something you enter in Meta. They are set once, by whoever runs the servers.'}
							</Text>
						</Box>
					</HStack>
					<HStack spacing="1" flexShrink={0}>
						{anyOperator && (
							<Button size="xs" variant="ghost" leftIcon={<Icon as={FiCopy} />} onClick={copy}>
								Copy for your operator
							</Button>
						)}
						<Button
							size="xs"
							variant="ghost"
							rightIcon={<Icon as={open ? FiChevronUp : FiChevronDown} />}
							onClick={() => setOpen((v) => !v)}
						>
							{open ? 'Hide' : 'Details'}
						</Button>
					</HStack>
				</HStack>

				<Collapse in={open} animateOpacity>
					<VStack align="stretch" spacing="1" mt="3">
						{items.map((item) => (
							<Row key={item.id} item={item} />
						))}
					</VStack>
					<Text fontSize="0.65rem" color="text.muted" mt="3">
						Your WhatsApp token and API URL live in Admin Settings → Secrets, under the service{' '}
						<Code fontSize="0.65rem">whatsapp</Code>. The server lines are variables: the CRM reads{' '}
						<Code fontSize="0.65rem">CRM_PRODUCT_API_URL</Code> and{' '}
						<Code fontSize="0.65rem">SERVICE_SHARED_SECRET</Code>; the connection service reads{' '}
						<Code fontSize="0.65rem">TENANT_API_URL</Code> and the{' '}
						<Code fontSize="0.65rem">META_*</Code> settings. No Meta credential is an environment variable.
					</Text>
				</Collapse>
			</Box>
		</Card>
	);
}
